
import { useSelector,useDispatch } from 'react-redux';
import { visualizerActions,uiActions } from "../../store";
import { useNavigate, useParams } from 'react-router-dom';

import TopMenu from '../TopMenu/TopMenu';
import ShareModal from '../ShareModal/ShareModal';
import EmailShareModal from '../EmailShareModal/EmailShareModal';
import HelpModal from '../HelpModal/HelpModal';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import ProductZoomModal from '../ProductZoomModal/ProductZoomModal';

import ViewInRoomModal from '../ViewInRoomModal/ViewInRoomModal';
import VisualizerHelp from '../VisualizerHelp/VisualizerHelp';
import PatternView from '../PatternView/PatternView';
import ProductMenu from '../ProductMenu/ProductMenu';

import styles from './VisualizerPage.module.css';
import { useEffect } from 'react';
import ProductLimitModal from '../ProductLimitModal/ProductLimitModal';

import VisualizerControls from '../VisualizerControls/VisualizerControls';

export default function VisualizerPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();

    const activePattern = useSelector((state) => state.visualizer.activePattern);
    const patternData = useSelector((state) => state.visualizer.patternData);
    const selectedSlotProducts = useSelector((state) => state.visualizer.selectedSlotProducts);
    const productDataBySeriesId = useSelector((state) => state.visualizer.productDataBySeriesId);

    // Helper function to find product by ID across all series
    const findProductById = (productId) => {
        for (const seriesId in productDataBySeriesId) {
            const products = productDataBySeriesId[seriesId];
            const product = products.find(p => p.id == productId);
            if (product) {
                return product;
            }
        }
        return null;
    };

    // Handle URL parameters when component loads
    useEffect(() => {
        const { patternId, productIds } = params;
        
        if (patternId && patternData.length > 0 && Object.keys(productDataBySeriesId).length > 0) {
            // Find pattern by ID
            const pattern = patternData.find(p => p.id == patternId || p.description == patternId);
            
            if (pattern && (!activePattern || activePattern.id != patternId)) {
                // Set active pattern
                dispatch(visualizerActions.setActivePattern(pattern));
                
                // Handle product IDs
                if (productIds && productIds !== '-1') {
                    const productIdArray = productIds.split(',');
                    
                    // Clear existing selections first
                    dispatch(visualizerActions.clearAllSlots());
                    
                    // Set selected products for each slot
                    productIdArray.forEach((productId, index) => {
                        if (productId && productId !== '-1') {
                            const slotIndex = index;
                            // Find the full product object
                            const fullProduct = findProductById(parseInt(productId));
                            
                            if (fullProduct) {
                                dispatch(visualizerActions.setSlotProduct({ 
                                    slotIndex, 
                                    product: fullProduct
                                }));
                            }
                        }
                    });
                } else {
                    // Clear selections for new pattern
                    dispatch(visualizerActions.clearAllSlots());
                }
            }
        }
    }, [params, patternData, productDataBySeriesId, dispatch, activePattern]);

    // Update URL when selected products change
    useEffect(() => {
        if (activePattern && Object.keys(selectedSlotProducts).length > 0) {
            const patternId = activePattern.id || activePattern.description;
            const productIds = Object.values(selectedSlotProducts)
                .filter(product => product !== null && product !== undefined)
                .map(product => product.id || product.productId || product)
                .join(',') || '-1';
            
            const newUrl = `/visualizer/pattern/${patternId}/products/${productIds}`;
            
            // Only update if URL is different
            if (window.location.pathname !== newUrl) {
                window.history.replaceState(null, '', newUrl);
            }
        }
    }, [selectedSlotProducts, activePattern]);

    const handleNewPattern = () => {
        dispatch(visualizerActions.clearAllSlots());
        navigate('/');
    };

    // Add keyboard shortcuts for undo/redo
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Check for Ctrl+Z (undo) or Cmd+Z on Mac
            if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                dispatch(visualizerActions.undoColorSelection());
            }
            // Check for Ctrl+Y (redo) or Ctrl+Shift+Z or Cmd+Shift+Z on Mac
            else if (
                ((event.ctrlKey || event.metaKey) && event.key === 'y') ||
                ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Z')
            ) {
                event.preventDefault();
                dispatch(visualizerActions.redoColorSelection());
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [dispatch]);

    const shareModalStatus = useSelector((state) => state.ui.shareModalActive);
    const emailModalStatus = useSelector((state) => state.ui.emailModalActive);
    const helpModalStatus = useSelector((state) => state.ui.helpModalActive);
    const feedbackModalStatus = useSelector((state) => state.ui.feedbackModalActive);
    const productZoomModalStatus = useSelector((state) => state.ui.productZoomModalActive);
    const productLimitModalStatus = useSelector((state) => state.ui.productLimitModalActive);
    const viewInRoomModalStatus = useSelector((state) => state.ui.viewInRoomModalActive);
    const visualizerHelpModalStatus = useSelector((state) => state.ui.visualizerHelpModalActive);
    

    useEffect(() => {
        // Only redirect to home if no pattern ID in URL and no active pattern
        const { patternId } = params;
        if (!patternId && activePattern === null) {
            navigate(`/`);
        }
    }, [activePattern, params, navigate]);

    // Show loading or redirect if no pattern and no URL params
    if (activePattern === null && !params.patternId) return false;

    return (
        <>
            <TopMenu extended={true} />
            
            <section className={styles.visualizerView}>
                <div className={styles.content}>
                    
                    <div className={styles.patternDiv}>
                        <ul className={styles.topDiv}>
                        <li>
                            <button onClick={handleNewPattern} className={styles.newPatternButton}>New Pattern</button>
                        </li>
                        <VisualizerControls/>
                    </ul>
                        <PatternView />
                    </div>
                    <div className={styles.productsDiv}>
                        <ProductMenu />
                    </div>
                </div>
            </section>

            {shareModalStatus && <ShareModal/>}
            {emailModalStatus && <EmailShareModal />}
            {helpModalStatus && <HelpModal />}
            {feedbackModalStatus && <FeedbackModal />}
            {productZoomModalStatus && <ProductZoomModal />}
            {productLimitModalStatus && <ProductLimitModal/>}
            {viewInRoomModalStatus && <ViewInRoomModal/>}
            {visualizerHelpModalStatus && <VisualizerHelp/>}
            
            
        </>
    );
}

